import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  PushpinOutlined,
  SettingOutlined,
  UnorderedListOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import {
  ContextButton,
  ExportButton,
  IconButton,
  IconContextButton,
  ScopeButton,
} from '@monorepo/shared-lib';
import {
  Col,
  Pagination,
  Popover,
  Row,
  Segmented,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import { ReactComponent as ConnectIcon } from 'assets/images/icons/connectIcon.svg';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as EyeIcon } from 'assets/images/icons/eyeIcon.svg';
import { ReactComponent as IconExport } from 'assets/images/icons/iconExport.svg';
import { ReactComponent as IconSupport } from 'assets/images/icons/iconSupport.svg';
import { ReactComponent as RefreshIcon } from 'assets/images/icons/refreshIcon.svg';
import { ReactComponent as UserIcon } from 'assets/images/icons/userIcon.svg';
import {
  FeatureDotBadge,
  useFeatureDot,
} from 'components/Custom/FeatureDotBadge';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import { BoldNameSpan, ColoredTag, StrongTag } from 'components/Custom/spans';
import { ColumnCustomizationDrawer } from 'components/Custom/ColumnCustomizationDrawer';
import {
  ScopeTable,
  scopeTablePageSizeSelectProps,
} from 'components/Custom/table';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { applyFilters as applyFiltersPipeline } from 'filter-system';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { TruncatedText } from 'components/Custom/TruncatedText';
import {
  EntityActionsCell,
  EntityCaptureCell,
  EntityCaptureContext,
  EntityConnectionCell,
  EntityCountryCell,
  EntityMenuContext,
  EntityOrgCell,
  EntityPinContext,
  EntityReturnsCell,
  EntityTableContext,
  EntityUsersCell,
  EntityVatCell,
  entityCellApi,
} from './EntityTableContext';
import { EmptyPage } from 'containers/EmptyPage';
import { MessageStates } from 'containers/MessageBox';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import {
  ENTITIES_COLUMNS_CONFIG,
  ENTITIES_VIEW_LAYOUT,
  PAGE_SIZE_ENTITIES,
} from 'pages/organisations';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FEATURE_DOT_COLOR } from 'shared/config/featureDotsConfig';
import { AccountType, Provider, UpgradeMessageSource } from 'store/api/enums';
import {
  turnDrawerOff,
  turnDrawerOn,
  turnMessageOn,
  turnModalOff,
  turnModalOn,
} from 'store/app/actions';
import {
  AdvancedUserRightsCodes,
  DrawerType,
  EntityStatus,
  WindowType,
} from 'store/app/enums';
import { getDrawerData, getDrawerOpen } from 'store/app/selectors';
import { DrawerData, MessageData } from 'store/app/types';
import { User } from 'store/auth/models';
import { Country } from 'store/common/models';
import {
  deleteEntityGroupRequest,
  disconnectOrganisationsRequest,
  fetchExcelFilesMetadataRequest,
  reactivateOrganisationRequest,
} from 'store/organisation/actions';
import { Entity, ReceiptEntityStatistics } from 'store/organisation/models';
import {
  getOrgMappings,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { getUserLoading, getUserMembers } from 'store/user/selectors';
import styled from 'styled-components';
import { AdddReturnButton } from 'utils/AddReturnButton';
import { AvatarGroupRenderer } from 'utils/avatar-group-renderer';
import { canEditTaxReturn } from 'utils/canEditTaxReturn';
import { clearOrganisationDetailsTabStorage } from 'utils/clear-organisation-tab-storage';
import { exportEntitiesToExcel } from 'utils/export-entities-to-excel';
import {
  isReceiptsEntityProvider,
  shouldShowEntityCaptureEnable,
} from 'utils/entity-capture-column';
import { findReceiptEntityStats } from 'utils/receipt-statistics';
import { useReceiptStatisticsAll } from 'utils/useReceiptStatisticsAll';
import { getReceiptStatisticsAll } from 'store/organisation/selectors';
import { EntityCaptureStatsCell } from './EntityCaptureStatsCell';
import { computeCaptureFileCountsFromMetadata } from 'components/OrganisationDetails/transactions-tab/entity-transaction-helpers';
import { shouldSkipExcelFilesMetadataBurst } from 'utils/excelFilesMetadataFetchGuard';
import { getEntityOrganisationPath } from 'utils/get-entity-organisation-path';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { buildPwaEntityDetailsNavigateUrl } from 'utils/pwa-nav';
import { navigateToEntityCaptureTab } from 'utils/navigate-to-entity-capture';
import { shouldShowCaptureAndTransactionsTabs } from 'utils/captureTransactionsAccess';
import { shouldRestrictDisconnectedProviderTabs } from 'utils/get-provider-type';
import { Preference, PreferenceActions } from 'utils/preferences';
import { PINNED_ENTITY_IDS_PREF } from 'utils/use-pwa-capture-entities';
import { AvatarSizes } from 'utils/render-avatar';
import { EntityGuide } from './guide';
import {
  CUSTOMISE_COLUMNS_ICON_FONT_SIZE,
  CustomiseColumnsIconButtonWithTooltip,
  customiseColumnsIconButtonStyle,
} from 'components/Custom/customise-columns-icon-button';
import {
  CardValueActionsRow,
  EntityCard,
  entityCardReturnTagStyle,
} from './EntityCard';

export const ENTITY_TUTORIAL_HIDE = 'entity-tutorial-hide';
const PINNED_ENTITY_IDS = PINNED_ENTITY_IDS_PREF;

const ENTITIES_ALWAYS_FIXED_COLUMNS = ['organisation', 'actions'];

type EntitiesColumnsPersistConfig = {
  visibleColumns: string[];
  lockedColumns?: string[];
  columnOrder?: string[];
};

const EntityOrgLink = styled(Link)`
  && {
    text-decoration: none;
    color: var(--color-text-base, #2e2e2e);
  }
  &&:hover {
    text-decoration: underline;
    color: var(--color-text-base, #2e2e2e);
  }
`;

interface EntityComponentProps {
  loading: boolean;
  mappedOrganisations?: Entity[];
  countries: Country[];
  addReturnMapping: (connectionId: string) => void;
  editReturnMapping: (connectionId: string, mappingId: string) => void;
  addEntity: () => void;
  createGroup: () => void;
  editGroup: (entity: Entity) => void;
  //connectEntity: () => void;
  editUsers: (entity: Entity) => void;
  currentUser?: User;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
  hasAdvancedUserRights: boolean;
}

export const EntityComponent: React.FC<EntityComponentProps> = ({
  loading,
  mappedOrganisations,
  countries,
  addReturnMapping,
  editReturnMapping,
  addEntity,
  createGroup,
  editGroup,
  //connectEntity,
  editUsers,
  currentUser,
  handlePreferences,
  hasAdvancedUserRights,
}) => {
  const pwaMobileNav = usePwaMobileNavEnabled();
  const navigate = useNavigate();
  const organisations = useSelector(getOrgOrganisations);
  const members = useSelector(getUserMembers);
  const membersLoading = useSelector(getUserLoading);

  const getEntityDetailsPath = React.useCallback(
    (organisationId: string) => {
      if (!pwaMobileNav) {
        return getEntityOrganisationPath(organisationId);
      }
      const org = organisations?.find((o) => o.uuid === organisationId);
      if (org) {
        return buildPwaEntityDetailsNavigateUrl(org);
      }
      return getEntityOrganisationPath(organisationId);
    },
    [pwaMobileNav, organisations]
  );

  const handleEntityDetailsNavClick = React.useCallback(
    (organisationId: string) => {
      if (pwaMobileNav) {
        const org = organisations?.find((o) => o.uuid === organisationId);
        if (
          org &&
          shouldShowCaptureAndTransactionsTabs(org.providerName) &&
          !shouldRestrictDisconnectedProviderTabs(org)
        ) {
          navigateToEntityCaptureTab();
        } else {
          clearOrganisationDetailsTabStorage();
        }
      } else {
        clearOrganisationDetailsTabStorage();
      }
    },
    [pwaMobileNav, organisations]
  );

  const openEntityDetails = React.useCallback(
    (organisationId: string) => {
      handleEntityDetailsNavClick(organisationId);
      navigate(getEntityDetailsPath(organisationId));
    },
    [getEntityDetailsPath, handleEntityDetailsNavClick, navigate]
  );

  const handlePwaEntityCardClick = React.useCallback(
    (event: React.MouseEvent, organisationId: string) => {
      const target = event.target as HTMLElement;
      if (target.closest('button, a, [role="button"], .ant-dropdown')) {
        return;
      }
      openEntityDetails(organisationId);
    },
    [openEntityDetails]
  );

  const pageSizeSaved = handlePreferences(PreferenceActions.GET, {
    name: PAGE_SIZE_ENTITIES,
  });
  const guidePanelHide = handlePreferences(PreferenceActions.GET, {
    name: ENTITY_TUTORIAL_HIDE,
  });
  const rawViewLayout = handlePreferences(PreferenceActions.GET, {
    name: ENTITIES_VIEW_LAYOUT,
  });
  const savedViewLayout =
    rawViewLayout === 'card' || rawViewLayout === 'list'
      ? rawViewLayout
      : undefined;
  const { t } = useTranslation();
  const { localizeCountry, localizeCountryLabel, localizeReturnLabel } =
    useCountryLabel();
  const dispatch = useDispatch();
  const drawerOpen = useSelector(getDrawerOpen);
  const drawerData = useSelector(getDrawerData);
  const entitiesColumnsDrawerOpen =
    drawerOpen && drawerData?.type === DrawerType.CUSTOMIZE_COLUMNS;
  const userChangedViewLayoutRef = React.useRef(false);
  const isClientUser =
    !!currentUser &&
    AccountType[currentUser.accountType] === AccountType.CLIENT;

  const defaultEntityColumnKeys = useMemo(() => {
    const keys = [
      'organisation',
      'returns',
      'captureStats',
      'users',
      'vatNumber',
      'country',
      'client',
      'actions',
    ];
    return isClientUser ? keys.filter((key) => key !== 'users') : keys;
  }, [isClientUser]);

  const defaultVisibleEntityColumnKeys = useMemo(
    () => defaultEntityColumnKeys.filter((key) => key !== 'country'),
    [defaultEntityColumnKeys]
  );

  const [visibleColumns, setVisibleColumns] = React.useState<string[] | null>(
    null
  );
  const [lockedColumns, setLockedColumns] = React.useState<string[]>([]);
  const [columnOrder, setColumnOrder] = React.useState<string[] | undefined>(
    undefined
  );

  React.useEffect(() => {
    const raw = handlePreferences(PreferenceActions.GET, {
      name: ENTITIES_COLUMNS_CONFIG,
    }) as EntitiesColumnsPersistConfig | undefined;

    const validKeys = new Set(defaultEntityColumnKeys);
    if (raw?.visibleColumns?.length) {
      setVisibleColumns(raw.visibleColumns.filter((key) => validKeys.has(key)));
      setLockedColumns(
        (raw.lockedColumns ?? []).filter((key) => validKeys.has(key))
      );
      const ordered = (raw.columnOrder ?? []).filter((key) =>
        validKeys.has(key)
      );
      setColumnOrder(ordered.length > 0 ? ordered : undefined);
    } else {
      setVisibleColumns(null);
      setLockedColumns([]);
      setColumnOrder(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when column keys change
  }, [defaultEntityColumnKeys]);

  const handleColumnsChange = useCallback(
    (config: {
      visibleColumns: string[];
      lockedColumns: string[];
      columnOrder: string[];
    }) => {
      const validKeys = new Set(defaultEntityColumnKeys);
      const fixedKeys = new Set(ENTITIES_ALWAYS_FIXED_COLUMNS);
      const visibleWithoutFixed = config.visibleColumns.filter(
        (key) => validKeys.has(key) && !fixedKeys.has(key)
      );
      const sanitized: EntitiesColumnsPersistConfig = {
        visibleColumns: ['organisation', ...visibleWithoutFixed, 'actions'],
        lockedColumns: (config.lockedColumns ?? []).filter(
          (key) => validKeys.has(key) && !fixedKeys.has(key)
        ),
        columnOrder: (config.columnOrder ?? []).filter(
          (key) => validKeys.has(key) && !fixedKeys.has(key)
        ),
      };

      setVisibleColumns(sanitized.visibleColumns);
      setLockedColumns(sanitized.lockedColumns ?? []);
      setColumnOrder(
        sanitized.columnOrder && sanitized.columnOrder.length > 0
          ? sanitized.columnOrder
          : undefined
      );

      handlePreferences(PreferenceActions.SET, {
        name: ENTITIES_COLUMNS_CONFIG,
        value: sanitized,
      });
    },
    [defaultEntityColumnKeys, handlePreferences]
  );

  const effectiveVisibleColumns = useMemo(() => {
    const base = visibleColumns ?? defaultVisibleEntityColumnKeys;
    const validKeys = new Set(defaultEntityColumnKeys);
    const fixedKeys = new Set(ENTITIES_ALWAYS_FIXED_COLUMNS);
    const middle = Array.from(
      new Set(base.filter((key) => validKeys.has(key) && !fixedKeys.has(key)))
    );
    return ['organisation', ...middle, 'actions'];
  }, [visibleColumns, defaultEntityColumnKeys, defaultVisibleEntityColumnKeys]);

  const openEntitiesColumnsDrawerRef = React.useRef<(() => void) | null>(null);

  const getEntityColumnTitle = useCallback(
    (key: string) => {
      switch (key) {
        case 'returns':
          return t('main.taxReturn');
        case 'captureStats':
          return t('organisation.capture-statistics-column');
        case 'users':
          return t('main.users');
        case 'vatNumber':
          return t('organisation.entity-tax-vat-column');
        case 'country':
          return t('common.country');
        case 'client':
          return t('organisation.connection-status');
        default:
          return key;
      }
    },
    [t]
  );

  const customizableEntityColumnKeys = useMemo(
    () =>
      defaultEntityColumnKeys.filter(
        (key) => !ENTITIES_ALWAYS_FIXED_COLUMNS.includes(key)
      ),
    [defaultEntityColumnKeys]
  );

  const openEntitiesColumnsDrawer = useCallback(() => {
    const customizableColumns = customizableEntityColumnKeys.map((key) => ({
      key,
      title: getEntityColumnTitle(key),
    }));

    const drawerData: DrawerData = {
      title: t('main.customise'),
      type: DrawerType.CUSTOMIZE_COLUMNS,
      component: (
        <ColumnCustomizationDrawer
          columns={customizableColumns}
          visibleColumns={effectiveVisibleColumns.filter(
            (key) => !ENTITIES_ALWAYS_FIXED_COLUMNS.includes(key)
          )}
          lockedColumns={lockedColumns}
          columnOrder={columnOrder}
          alwaysFixedColumns={ENTITIES_ALWAYS_FIXED_COLUMNS}
          onSave={handleColumnsChange}
          onClose={() => dispatch(turnDrawerOff())}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  }, [
    columnOrder,
    customizableEntityColumnKeys,
    dispatch,
    effectiveVisibleColumns,
    getEntityColumnTitle,
    handleColumnsChange,
    lockedColumns,
    t,
  ]);

  React.useLayoutEffect(() => {
    openEntitiesColumnsDrawerRef.current = openEntitiesColumnsDrawer;
  }, [openEntitiesColumnsDrawer]);

  const [viewMode, setViewMode] = React.useState<'list' | 'card'>(() => {
    if (pwaMobileNav) {
      return 'card';
    }
    return savedViewLayout === 'card' || savedViewLayout === 'list'
      ? savedViewLayout
      : 'list';
  });

  React.useEffect(() => {
    if (pwaMobileNav && viewMode !== 'card') {
      setViewMode('card');
    }
  }, [pwaMobileNav, viewMode]);

  React.useEffect(() => {
    if (pwaMobileNav || userChangedViewLayoutRef.current) return;
    if (savedViewLayout === 'card' || savedViewLayout === 'list') {
      setViewMode(savedViewLayout);
    }
  }, [savedViewLayout, pwaMobileNav]);
  const mappings = useSelector(getOrgMappings);
  const receiptStatistics = useSelector(getReceiptStatisticsAll);
  useReceiptStatisticsAll();

  useEffect(() => {
    if (!receiptStatistics?.entitiesStats?.length || !organisations?.length) {
      return;
    }

    const requestedOrgIds = new Set<string>();
    for (const entityStat of receiptStatistics.entitiesStats) {
      const org = organisations.find(
        (candidate) =>
          candidate.connectionId === entityStat.applicationConnectionId ||
          candidate.uuid === entityStat.applicationConnectionId
      );
      if (!org || org.excelFilesMetadata !== undefined) continue;
      if (
        entityStat.filesPendingReview <= 0 &&
        entityStat.filesReviewed <= 0 &&
        entityStat.activeFiles <= 0
      ) {
        continue;
      }
      if (requestedOrgIds.has(org.uuid)) continue;
      if (shouldSkipExcelFilesMetadataBurst(org.uuid)) continue;

      requestedOrgIds.add(org.uuid);
      dispatch(fetchExcelFilesMetadataRequest(org.uuid));
    }
  }, [dispatch, organisations, receiptStatistics?.entitiesStats]);

  const statsByConnectionId = useMemo(() => {
    const map = new Map<string, ReceiptEntityStatistics>();
    receiptStatistics?.entitiesStats?.forEach((entityStats) => {
      map.set(entityStats.applicationConnectionId, entityStats);
    });
    return map;
  }, [receiptStatistics]);
  const [pinnedEntityIds, setPinnedEntityIds] = useState<string[]>([]);
  const [openContextMenuId, setOpenContextMenuId] = useState<string | null>(
    null
  );
  const [filtersState, setFiltersState] = useState<Record<string, any>>({});
  const [cardPage, setCardPage] = useState(1);
  // const [taxReturnNames, setTaxReturnNames] = useState([]);
  const [showCard, setShowCard] = React.useState(!guidePanelHide);

  useEffect(() => {
    const stored = handlePreferences(PreferenceActions.GET, {
      name: PINNED_ENTITY_IDS,
    });
    if (Array.isArray(stored)) setPinnedEntityIds(stored);
  }, []);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const error = queryParams?.get('error');

  useEffect(() => {
    if (error) {
      const message: MessageData = {
        title: t('common.error'),
        description: t(`organisation.${error}`),
        type: MessageStates.ERROR,
      };
      dispatch(turnMessageOn(message));
    }
  }, [error]);

  const entityIdSetKey = useMemo(() => {
    if (!mappedOrganisations?.length) return '';
    return mappedOrganisations
      .map((entity) => entity.organisationId)
      .sort()
      .join('\0');
  }, [mappedOrganisations]);

  const dataSource = useMemo(() => {
    const list = [...(mappedOrganisations || [])];
    list.sort((a, b) => {
      const aPinned = pinnedEntityIds.indexOf(a.organisationId);
      const bPinned = pinnedEntityIds.indexOf(b.organisationId);
      if (aPinned >= 0 && bPinned >= 0) return aPinned - bPinned;
      if (aPinned >= 0) return -1;
      if (bPinned >= 0) return 1;
      return a.organisation.localeCompare(b.organisation);
    });
    return list;
  }, [mappedOrganisations, pinnedEntityIds]);

  useEffect(() => {
    setFiltersState({});
    setCardPage(1);
  }, [entityIdSetKey]);

  const onAddReturn = (connectionId: string) => {
    addReturnMapping(connectionId);
  };

  const handleExportEntitiesList = useCallback(() => {
    if (!organisations?.length) return;
    void exportEntitiesToExcel(
      organisations,
      countries,
      mappings,
      t,
      statsByConnectionId
    );
  }, [organisations, countries, mappings, t, statsByConnectionId]);

  const renderCaptureStats = useCallback(
    (record: Entity) => {
      const stats = findReceiptEntityStats(receiptStatistics?.entitiesStats, {
        connectionId: record.connectionId,
        organisationId: record.organisationId,
      });
      const organisation = organisations?.find(
        (org) => org.uuid === record.organisationId
      );
      const fileCountsFromMetadata =
        organisation?.excelFilesMetadata !== undefined
          ? computeCaptureFileCountsFromMetadata(
              organisation.excelFilesMetadata,
              record.providerName
            )
          : undefined;

      return (
        <EntityCaptureStatsCell
          connectionId={record.connectionId}
          organisationId={record.organisationId}
          providerName={record.providerName}
          hideReadyToPost={isReceiptsEntityProvider(record.providerName)}
          showEnable={shouldShowEntityCaptureEnable(record, Boolean(stats))}
          stats={stats}
          fileCountsFromMetadata={fileCountsFromMetadata}
        />
      );
    },
    [organisations, receiptStatistics?.entitiesStats]
  );

  const renderSortIcon = useCallback(({ sortOrder }) => {
    if (sortOrder == 'ascend') {
      return (
        <ArrowDownOutlined
          style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
        />
      );
    } else {
      return (
        <ArrowUpOutlined
          style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
        />
      );
    }
  }, []);

  /** Wrap a comparator so pinned entities always stay at the top.
   *  AntD reverses the comparator result for descending sort (×-1),
   *  so we counter-reverse the pinned portion to keep them anchored. */
  const withPinnedFirst = useCallback(
    (compare: (a: Entity, b: Entity) => number) =>
      (a: Entity, b: Entity, sortOrder?: 'ascend' | 'descend') => {
        const aPinned = pinnedEntityIds.includes(a.organisationId);
        const bPinned = pinnedEntityIds.includes(b.organisationId);
        const descending = sortOrder === 'descend';
        if (aPinned && !bPinned) return descending ? 1 : -1;
        if (!aPinned && bPinned) return descending ? -1 : 1;
        if (aPinned && bPinned) {
          const pinOrder =
            pinnedEntityIds.indexOf(a.organisationId) -
            pinnedEntityIds.indexOf(b.organisationId);
          return descending ? -pinOrder : pinOrder;
        }
        return compare(a, b);
      },
    [pinnedEntityIds]
  );

  const renderOrgLink = (
    content: string,
    record: Entity,
    options?: { forCard?: boolean }
  ) => {
    const to = getEntityDetailsPath(record.organisationId);
    const isPinned = pinnedEntityIds.includes(record.organisationId);
    const forCard = options?.forCard;

    return (
      <Space
        size="small"
        style={
          forCard
            ? { width: '100%', minWidth: 0, maxWidth: '100%' }
            : { width: '100%', minWidth: 0 }
        }
      >
        {isPinned && (
          <PushpinOutlined
            style={{
              fontSize: 12,
              color: 'var(--color-primary, #41aaaf)',
              flexShrink: 0,
            }}
            title={t('organisation.unpin-entity')}
          />
        )}
        <ProviderLogoNav
          providerName={record.providerName}
          shortCode={record.shortCode}
          organisationId={record.organisationId}
          style={{ height: '32px', display: 'block', flexShrink: 0 }}
        >
          {record?.providerName?.toLowerCase() === 'group' ? (
            <UsergroupAddOutlined
              style={{
                fontSize: 20,
                color: 'var(--color-on-logo-tile, #042a2a)',
                paddingTop: '4px',
              }}
            />
          ) : (
            <ProviderIcon
              providerName={record?.providerName}
              alt={record?.providerName}
              width={25}
              style={{ paddingTop: '4px' }}
            />
          )}
        </ProviderLogoNav>
        <EntityOrgLink
          to={to}
          onClick={() => handleEntityDetailsNavClick(record.organisationId)}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            display: 'block',
          }}
        >
          <TruncatedText
            style={{
              fontSize: '13px',
              maxWidth: '100%',
            }}
          >
            {content}
          </TruncatedText>
        </EntityOrgLink>
      </Space>
    );
  };

  const formatProviderLabel = (providerName?: string) => {
    if (!providerName) return '';
    if (providerName.toLowerCase() === 'group') return t('common.group');
    return (
      providerName.charAt(0).toUpperCase() + providerName.slice(1).toLowerCase()
    );
  };

  const renderEntityCardIdentity = (record: Entity) => {
    const to = getEntityDetailsPath(record.organisationId);
    const isPinned = pinnedEntityIds.includes(record.organisationId);
    const subtitle = formatProviderLabel(record.providerName);

    const nameStyle: React.CSSProperties = {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '22px',
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };

    const nameNode = (
      <BoldNameSpan style={nameStyle}>{record.organisation}</BoldNameSpan>
    );

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          minWidth: 0,
          width: '100%',
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <ProviderLogoNav
            providerName={record.providerName}
            shortCode={record.shortCode}
            organisationId={record.organisationId}
            onClick={
              pwaMobileNav
                ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openEntityDetails(record.organisationId);
                  }
                : undefined
            }
            style={{
              height: 40,
              width: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {record?.providerName?.toLowerCase() === 'group' ? (
              <UsergroupAddOutlined
                style={{
                  fontSize: 22,
                  color: 'var(--color-on-logo-tile, #042a2a)',
                }}
              />
            ) : (
              <ProviderIcon
                providerName={record?.providerName}
                alt={record?.providerName}
                width={28}
              />
            )}
          </ProviderLogoNav>
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            rowGap: 4,
          }}
        >
          <EntityOrgLink
            to={to}
            onClick={() => handleEntityDetailsNavClick(record.organisationId)}
            style={{ color: 'inherit', minWidth: 0, overflow: 'hidden' }}
          >
            {nameNode}
          </EntityOrgLink>
          {subtitle && (
            <BoldNameSpan //@ts-ignore
              fontWeight={400}
              style={{
                fontSize: 13,
                lineHeight: '18px',
                color: 'var(--color-text-muted, #5a6a6a)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subtitle}
            </BoldNameSpan>
          )}
          <Space size={[6, 6]} wrap align="center" style={{ marginTop: 6 }}>
            {isPinned && (
              <PushpinOutlined
                style={{ fontSize: 12, color: 'var(--color-primary, #41aaaf)' }}
                title={t('organisation.unpin-entity')}
              />
            )}
            {renderConnectionStatus(record)}
          </Space>
        </div>
      </div>
    );
  };

  const renderRemainingReturns = (record: Entity, connectionId, returnArr) => {
    return (
      <Space direction="vertical" style={{ rowGap: '0' }}>
        {returnArr.map((map) => {
          return (
            <React.Fragment key={map.mappingId}>
              {canEditTaxReturn(
                record.connectedProperly,
                currentUser,
                map.dataLog?.status
              ) &&
              currentUser &&
              (!hasAdvancedUserRights ||
                hasAdvancedUserRight(
                  currentUser,
                  record,
                  AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                )) ? (
                <StyledTooltip placement="top" title={t('main.click-to-edit')}>
                  <StrongTag
                    onClick={() =>
                      editReturnMapping(connectionId, map.mappingId)
                    }
                  >
                    {localizeReturnLabel(map.mappingName)}
                  </StrongTag>
                </StyledTooltip>
              ) : (
                <StyledTooltip
                  placement="top"
                  title={
                    !record.connectedProperly
                      ? t('organisation.entity-not-connected-description')
                      : !currentUser ||
                        (hasAdvancedUserRights &&
                          !hasAdvancedUserRight(
                            currentUser,
                            record,
                            AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                          ))
                      ? t('global.edit-return-rights-warning')
                      : t(
                          'organisation.edit-tax-return-disabled-submission-tooltip'
                        )
                  }
                >
                  <StrongTag
                    className={
                      !record.connectedProperly
                        ? 'strong-tag-disabled'
                        : undefined
                    }
                    style={
                      !record.connectedProperly
                        ? {
                            color: 'var(--color-tag-disabled, #5a6a6a)',
                            cursor: 'not-allowed',
                          }
                        : undefined
                    }
                  >
                    {localizeReturnLabel(map.mappingName)}
                  </StrongTag>
                </StyledTooltip>
              )}
            </React.Fragment>
          );
        })}
      </Space>
    );
  };

  const renderReturnTag = (
    record: Entity,
    mappedReturn: Entity['returns'][number],
    tagStyle?: React.CSSProperties
  ) => {
    const canEdit =
      canEditTaxReturn(
        record.connectedProperly,
        currentUser,
        mappedReturn.dataLog?.status
      ) &&
      currentUser &&
      (!hasAdvancedUserRights ||
        hasAdvancedUserRight(
          currentUser,
          record,
          AdvancedUserRightsCodes.CREATE_EDIT_RETURN
        ));

    const disabledStyle = !record.connectedProperly
      ? {
          color: 'var(--color-tag-disabled, #5a6a6a)',
          cursor: 'not-allowed' as const,
        }
      : {};

    const tooltipTitle = !record.connectedProperly
      ? t('organisation.entity-not-connected-description')
      : !currentUser ||
        (hasAdvancedUserRights &&
          !hasAdvancedUserRight(
            currentUser,
            record,
            AdvancedUserRightsCodes.CREATE_EDIT_RETURN
          ))
      ? t('global.edit-return-rights-warning')
      : t('organisation.edit-tax-return-disabled-submission-tooltip');

    if (canEdit) {
      return (
        <StyledTooltip placement="top" title={t('main.click-to-edit')}>
          <StrongTag
            className={
              !record.connectedProperly ? 'strong-tag-disabled' : undefined
            }
            onClick={() =>
              editReturnMapping(record.connectionId, mappedReturn.mappingId)
            }
            style={{ ...tagStyle, ...disabledStyle }}
          >
            {localizeReturnLabel(mappedReturn.mappingName)}
          </StrongTag>
        </StyledTooltip>
      );
    }

    return (
      <StyledTooltip placement="top" title={tooltipTitle}>
        <StrongTag
          className={
            !record.connectedProperly ? 'strong-tag-disabled' : undefined
          }
          style={{ ...tagStyle, ...disabledStyle }}
        >
          {localizeReturnLabel(mappedReturn.mappingName)}
        </StrongTag>
      </StyledTooltip>
    );
  };

  const renderMappingLink = (
    _content: string,
    record: Entity,
    options?: { forCard?: boolean }
  ) => {
    const forCard = options?.forCard;

    if (forCard) {
      const visibleReturns = record.returns.slice(0, 2);
      const overflowCount = Math.max(0, record.returns.length - 2);

      return (
        <CardValueActionsRow>
          {visibleReturns.map((mappedReturn) => (
            <React.Fragment key={mappedReturn.mappingId}>
              {renderReturnTag(record, mappedReturn, entityCardReturnTagStyle)}
            </React.Fragment>
          ))}
          {overflowCount > 0 && (
            <Popover
              placement="topRight"
              arrow={false}
              trigger="click"
              content={renderRemainingReturns(
                record,
                record.connectionId,
                record.returns.slice(2)
              )}
              title=""
            >
              <StrongTag style={entityCardReturnTagStyle}>
                {overflowCount}
              </StrongTag>
            </Popover>
          )}
          <AdddReturnButton
            record={record}
            onClick={() => onAddReturn(record.connectionId)}
            toggleButton={true}
          />
        </CardValueActionsRow>
      );
    }

    return (
      <Space size="small" style={{ columnGap: 3, maxWidth: '100%' }} wrap>
        {record.returns.map((mappedReturn, index, returnArr) => {
          if (index <= 1) {
            return (
              <React.Fragment key={mappedReturn.mappingId}>
                {renderReturnTag(record, mappedReturn)}
              </React.Fragment>
            );
          }
          if (index === 2) {
            return (
              <Popover
                key={returnArr.length + 2}
                placement="topRight"
                arrow={false}
                trigger="click"
                content={renderRemainingReturns(
                  record,
                  record.connectionId,
                  record.returns.slice(index)
                )}
                title=""
              >
                <StrongTag key={returnArr.length}>
                  {returnArr.length - index}
                </StrongTag>
              </Popover>
            );
          }
          return null;
        })}
        <AdddReturnButton
          record={record}
          onClick={() => onAddReturn(record.connectionId)}
          toggleButton={true}
        />
      </Space>
    );
  };

  const renderTaxReturn = (
    content: string,
    record: Entity,
    options?: { forCard?: boolean }
  ) => {
    if (record.providerName?.toLowerCase() === 'group') {
      return null;
    }
    if (record.returns.length > 0) {
      return renderMappingLink(content, record, options);
    }
    const addButton = (
      <AdddReturnButton
        record={record}
        onClick={() => onAddReturn(record.connectionId)}
        toggleButton={options?.forCard}
      />
    );
    if (options?.forCard) {
      return (
        <CardValueActionsRow style={{ marginTop: 0 }}>
          {addButton}
        </CardValueActionsRow>
      );
    }
    return addButton;
  };

  const { showDot: showThreeDotsMarker, dismiss: dismissThreeDotsMarker } =
    useFeatureDot('entities-three-dots');
  const { dismiss: dismissPinMarker } = useFeatureDot('entities-pin');

  const renderContextMenu = (record) => {
    const to = getEntityDetailsPath(record.organisationId);

    const closeContextMenu = () => {
      setOpenContextMenuId(null);
    };

    const handleDeleteEntity = () => {
      closeContextMenu();
      if (record.providerName?.toLowerCase() === 'group') {
        dispatch(deleteEntityGroupRequest(record.organisationId));
        return;
      }
      dispatch(
        disconnectOrganisationsRequest(
          record.organisationId,
          false,
          EntityStatus.DELETED
        )
      );
    };

    const handleChangeToViewOnly = () => {
      closeContextMenu();
      dispatch(
        disconnectOrganisationsRequest(
          record.organisationId,
          false,
          EntityStatus.VIEW_ONLY
        )
      );
    };

    const handleReactivateEntity = () => {
      closeContextMenu();
      dispatch(reactivateOrganisationRequest(record.organisationId));
    };

    const showDeleteConfirmation = () => {
      closeContextMenu();
      const isViewOnly = record.entityStatus === EntityStatus.VIEW_ONLY;
      const messageSource = isViewOnly
        ? UpgradeMessageSource.DELETE_ENTITY_ONLY
        : UpgradeMessageSource.DELETE_ENTITY;

      dispatch(
        turnModalOn({
          title: '',
          type: WindowType.WARNING,
          component: (
            <UpgradeMessage
              source={messageSource}
              callback={handleDeleteEntity}
              onClose={() => dispatch(turnModalOff())}
              secondButtonCallback={
                isViewOnly ? undefined : handleChangeToViewOnly
              }
            />
          ),
        })
      );
    };

    const showChangeToViewOnlyConfirmation = () => {
      closeContextMenu();
      dispatch(
        turnModalOn({
          title: '',
          type: WindowType.WARNING,
          component: (
            <UpgradeMessage
              source={UpgradeMessageSource.CHANGE_TO_VIEW_ONLY}
              callback={handleChangeToViewOnly}
              onClose={() => dispatch(turnModalOff())}
            />
          ),
        })
      );
    };

    const showReactivateConfirmation = () => {
      closeContextMenu();
      dispatch(
        turnModalOn({
          title: '',
          type: WindowType.WARNING,
          component: (
            <UpgradeMessage
              source={UpgradeMessageSource.REACTIVATE_ENTITY}
              callback={handleReactivateEntity}
              onClose={() => dispatch(turnModalOff())}
            />
          ),
        })
      );
    };

    const isViewOnly = record.entityStatus === EntityStatus.VIEW_ONLY;
    const isGroup = record.providerName?.toLowerCase() === 'group';
    const isPinned = pinnedEntityIds.includes(record.organisationId);

    const handlePinUnpin = () => {
      closeContextMenu();
      dismissPinMarker();
      const next = isPinned
        ? pinnedEntityIds.filter((id) => id !== record.organisationId)
        : [...pinnedEntityIds, record.organisationId];
      setPinnedEntityIds(next);
      handlePreferences(PreferenceActions.SET, {
        name: PINNED_ENTITY_IDS,
        value: next,
      });
      const message: MessageData = {
        title: t('organisation.pin-entity-success-title'),
        description: isPinned
          ? t('organisation.unpin-entity-success')
          : t('organisation.pin-entity-success'),
        type: MessageStates.SUCCESS,
      };
      dispatch(turnMessageOn(message));
    };

    return (
      <div className="context-menu-items">
        <Space direction="vertical" style={{ rowGap: '0' }}>
          <FeatureDotBadge dotId="entities-pin">
            <IconContextButton
              type="text"
              icon={
                <PushpinOutlined style={{ width: '16px', height: '16px' }} />
              }
              onClick={handlePinUnpin}
            >
              {isPinned
                ? t('organisation.unpin-entity')
                : t('organisation.pin-entity')}
            </IconContextButton>
          </FeatureDotBadge>
          <IconContextButton
            type="text"
            icon={<ConnectIcon style={{ width: '16px', height: '17px' }} />}
            onClick={() => {
              closeContextMenu();
              onAddReturn(record.connectionId);
            }}
            disabled={
              isViewOnly ||
              isGroup ||
              !currentUser ||
              (hasAdvancedUserRights &&
                !hasAdvancedUserRight(
                  currentUser,
                  record,
                  AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                ))
            }
          >
            {t('organisation.add-tax-return')}
          </IconContextButton>
          <IconContextButton
            type="text"
            icon={<UserIcon style={{ width: '18px', height: '20px' }} />}
            onClick={() => {
              closeContextMenu();
              editUsers(record);
            }}
            disabled={isViewOnly}
          >
            {t('users.edit-user')}
          </IconContextButton>
          <IconContextButton
            type="text"
            icon={<EyeIcon style={{ width: '18px', height: '22px' }} />}
          >
            <Link
              style={{ textDecoration: 'none' }}
              to={to}
              onClick={() => {
                handleEntityDetailsNavClick(record.organisationId);
                closeContextMenu();
              }}
            >
              {t('organisation.view-entity')}
            </Link>
          </IconContextButton>
          {record.entityStatus === EntityStatus.VIEW_ONLY ? (
            <IconContextButton
              type="text"
              icon={<RefreshIcon style={{ width: '18px', height: '22px' }} />}
              onClick={showReactivateConfirmation}
            >
              {t('organisation.reactivate-entity')}
            </IconContextButton>
          ) : (
            <IconContextButton
              type="text"
              icon={
                <EyeInvisibleOutlined
                  style={{ width: '18px', height: '22px' }}
                />
              }
              onClick={showChangeToViewOnlyConfirmation}
            >
              {t('organisation.change-to-view-only')}
            </IconContextButton>
          )}
          {!pwaMobileNav ? (
            <IconContextButton
              type="text"
              icon={
                <DeleteOutlined style={{ width: '18px', height: '22px' }} />
              }
              onClick={showDeleteConfirmation}
              danger
            >
              {t('organisation.delete-entity')}
            </IconContextButton>
          ) : null}
        </Space>
      </div>
    );
  };

  const renderConnectionStatus = (record: Entity) => {
    if (record.entityStatus === EntityStatus.VIEW_ONLY) {
      return (
        <StyledTooltip title={t('organisation.view-only-state-tooltip')}>
          <ColoredTag
            color="var(--color-view-only-text, #5a6a6a)" //@ts-ignore
            bgcolor="var(--color-view-only-bg, #eaeaea)"
            bordered={true}
            bordercolor="var(--color-view-only-border, #d7d7d7)"
            icon={<LockOutlined />}
          >
            {t('organisation.export.status-view-only')}
          </ColoredTag>
        </StyledTooltip>
      );
    }

    const isGroup = record.providerName?.toLowerCase() === 'group';
    if (isGroup) {
      return (
        <ColoredTag
          color="var(--color-status-success, #058181)" //@ts-ignore
          bgcolor="var(--color-status-success-bg, #E9F6F7)"
          bordered={true}
          bordercolor="var(--color-border, #dbeae3)"
          icon={<CheckOutlined />}
        >
          {t('common.active')}
        </ColoredTag>
      );
    }

    if (record.connectedProperly)
      return (
        <ColoredTag
          color="var(--color-status-success, #058181)" //@ts-ignore
          bgcolor="var(--color-status-success-bg, #E9F6F7)"
          bordered={true}
          bordercolor="var(--color-border, #dbeae3)"
          icon={<CheckOutlined />}
        >
          {t('common.active')}
        </ColoredTag>
      );
    if (record.providerName === Provider.EXCEL)
      return (
        <ColoredTag
          color="var(--color-status-success, #058181)" //@ts-ignore
          bgcolor="var(--color-status-success-bg, #E9F6F7)"
          bordered={true}
          bordercolor="var(--color-border, #dbeae3)"
          icon={<CheckOutlined />}
        >
          {t('common.active')}
        </ColoredTag>
      );
    return (
      <IconButton
        type="default"
        style={{
          height: '30px',
          width: 'auto',
          maxWidth: '100%',
          borderRadius: 10,
          display: 'inline-flex',
          alignItems: 'center',
          padding: '0 8px',
          fontSize: '12px',
          lineHeight: '12px',
        }}
        icon={<RefreshIcon width={'16px'} height={'16px'} />}
        onClick={addEntity}
      >
        {t('common.reconnect')}
      </IconButton>
    );
  };

  const renderEntityActions = (record: Entity) => {
    if (
      !currentUser?.accountType ||
      AccountType[currentUser.accountType] === AccountType.CLIENT
    ) {
      return null;
    }
    const menuOpen = openContextMenuId === record.organisationId;
    const menuButton = (
      <ContextButton
        type="text"
        aria-label={t('common.more-actions')}
        onClick={(e) => {
          e?.stopPropagation?.();
          if (!menuOpen) {
            setOpenContextMenuId(record.organisationId);
            dismissThreeDotsMarker();
          }
        }}
      >
        <EllipseIcon
          style={{
            width: 18,
            height: 18,
          }}
        />
      </ContextButton>
    );

    return (
      <span style={{ position: 'relative', display: 'inline-block' }}>
        {showThreeDotsMarker && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: FEATURE_DOT_COLOR,
              zIndex: 1,
              pointerEvents: 'none',
            }}
            aria-hidden
          />
        )}
        {menuOpen ? (
          <Popover
            placement="bottomLeft"
            arrow={false}
            trigger="click"
            open
            content={renderContextMenu(record)}
            title={''}
            overlayStyle={{ zIndex: 1050 }}
            onOpenChange={(open) => {
              setOpenContextMenuId(open ? record.organisationId : null);
              if (open) {
                dismissThreeDotsMarker();
              }
            }}
          >
            {menuButton}
          </Popover>
        ) : (
          menuButton
        )}
      </span>
    );
  };

  const renderCountry = (record: Entity) =>
    localizeCountry(record.country, record.country);

  const renderVat = (content: string) => (
    <TruncatedText style={{ maxWidth: 200 }}>{content}</TruncatedText>
  );

  const renderUsers = (record: Entity, options?: { forCard?: boolean }) => {
    const avatars = (
      <AvatarGroupRenderer entity={record} size={AvatarSizes.DEFAULT} />
    );
    if (!options?.forCard) return avatars;
    return (
      <CardValueActionsRow style={{ marginTop: 0 }}>
        {avatars}
      </CardValueActionsRow>
    );
  };

  const showUsersSection = !(
    currentUser && AccountType[currentUser.accountType] === AccountType.CLIENT
  );

  const checkEntityMessage = () => {
    let message = 0;
    if (!dataSource || dataSource.length === 0) message = 1;
    /* if (
      !!dataSource &&
      dataSource.findIndex((ds) => ds.returns.length > 0) > -1
    )
      message = 0; */

    if (message === 1) {
      return (
        <EmptyPage
          titleText={t('organisation.empty-entities-title')}
          actionText={t('organisation.empty-entities-action')}
          buttonText={t('organisation.empty-entities-button')}
          actionCallback={addEntity}
          disableButton={!currentUser?.canManageOrgs}
          showIcon
        />
      );
    } else return;
    /* else if (message === 2) {
      return (
        <EmptyPage
          titleText={t('organisation.empty-tax-returns-title')}
          actionText={t('organisation.empty-tax-returns-action')}
          messageOnly
        />
      );
    } */
  };
  // const getTaxReturns = () => {
  //   const taxReturns: string[] = [];
  //   dataSource?.forEach((item) => {
  //     item.returns.forEach((tax) => taxReturns.push(tax.mappingName));
  //   });
  //   return taxReturns;
  // };

  entityCellApi.renderOrgLink = renderOrgLink;
  entityCellApi.renderTaxReturn = renderTaxReturn;
  entityCellApi.renderCaptureStats = renderCaptureStats;
  entityCellApi.renderUsers = renderUsers;
  entityCellApi.renderVat = renderVat;
  entityCellApi.renderCountry = renderCountry;
  entityCellApi.renderConnectionStatus = renderConnectionStatus;
  entityCellApi.renderEntityActions = renderEntityActions;

  const columns = useMemo(
    () => [
    {
      title: t('common.name'),
      dataIndex: 'organisation',
      key: 'organisation',
      width: 200,
      fixed: 'left' as const,
      className: 'entities-col-name',
      render: (content: string, record: Entity) => (
        <EntityOrgCell content={content} record={record} />
      ),
      shouldCellUpdate: (record: Entity, prev: Entity) => record !== prev,
      sorter: withPinnedFirst((a, b) =>
        a.organisation.localeCompare(b.organisation)
      ),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.taxReturn'),
      dataIndex: 'returns',
      key: 'returns',
      width: 365,
      className: 'entities-col-tax-return',
      render: (content: string, record: Entity) => (
        <EntityReturnsCell content={content} record={record} />
      ),
      shouldCellUpdate: (record: Entity, prev: Entity) => record !== prev,
      /*sorter: (a, b) => {
        a.returns.sort((a, b) => a.mappingName.localeCompare(b.mappingName));
        b.returns.sort((a, b) => a.mappingName.localeCompare(b.mappingName));
      },
      sortIcon: renderSortIcon,*/
    },
    {
      title: t('organisation.capture-statistics-column'),
      key: 'captureStats',
      width: 178,
      className: 'entities-col-capture',
      responsive: ['lg'] as const,
      render: (_content, record: Entity) => (
        <EntityCaptureCell record={record} />
      ),
      shouldCellUpdate: (record: Entity, prev: Entity) => record !== prev,
    },
    {
      title: t('main.users'),
      dataIndex: 'users',
      key: 'users',
      width: 120,
      className: 'entities-col-users',
      render: (_content, record: Entity) => <EntityUsersCell record={record} />,
      shouldCellUpdate: (record: Entity, prev: Entity) => record !== prev,
      responsive: ['lg'],
    },
    {
      title: t('organisation.entity-tax-vat-column'),
      dataIndex: 'vatNumber',
      key: 'vatNumber',
      width: 150,
      sorter: withPinnedFirst((a, b) =>
        (a.vatNumber ?? '').localeCompare(b.vatNumber ?? '')
      ),
      sortIcon: renderSortIcon,
      render: (content) => <EntityVatCell content={content} />,
      shouldCellUpdate: (record: Entity, prev: Entity) => record !== prev,
    },
    {
      title: t('common.country'),
      dataIndex: 'country',
      key: 'country',
      width: 160,
      render: (_content, record: Entity) => (
        <EntityCountryCell record={record} />
      ),
      shouldCellUpdate: (record: Entity, prev: Entity) => record !== prev,
      sorter: withPinnedFirst((a, b) => a.country.localeCompare(b.country)),
      sortIcon: renderSortIcon,
    },
    {
      title: t('organisation.connection-status'),
      dataIndex: 'client',
      key: 'client',
      width: 128,
      className: 'entities-col-connection',
      sortIcon: renderSortIcon,
      render: (_content, record: Entity) => (
        <EntityConnectionCell record={record} />
      ),
      shouldCellUpdate: (record: Entity, prev: Entity) => record !== prev,
      responsive: ['lg'],
    },
    {
      title: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            minHeight: 32,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              justifyContent: 'flex-end',
            }}
          >
            <CustomiseColumnsIconButtonWithTooltip
              type="default"
              icon={
                <SettingOutlined
                  style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
                />
              }
              style={customiseColumnsIconButtonStyle(entitiesColumnsDrawerOpen)}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                openEntitiesColumnsDrawerRef.current?.();
              }}
            />
          </div>
        </div>
      ),
      key: 'actions',
      width: 52,
      fixed: 'right' as const,
      align: 'right' as const,
      render: (_content, record: Entity) => (
        <EntityActionsCell record={record} />
      ),
      shouldCellUpdate: (record: Entity, prev: Entity) => record !== prev,
    },
  ],
    [t, withPinnedFirst, renderSortIcon, entitiesColumnsDrawerOpen]
  );

  const rawPageSize = pageSizeSaved;
  const pageSize =
    typeof rawPageSize === 'number' && rawPageSize > 0
      ? rawPageSize
      : typeof rawPageSize === 'string' && !Number.isNaN(Number(rawPageSize))
      ? Number(rawPageSize)
      : 10;

  const displayEntities = useMemo(() => {
    const base = dataSource ?? [];
    if (!filtersState || Object.keys(filtersState).length === 0) {
      return base;
    }
    return (
      applyFiltersPipeline(base, filtersState, {
        mainLocaleKey: 'organisation.filterLabels',
      }) ?? base
    );
  }, [dataSource, filtersState]);

  useEffect(() => {
    setCardPage(1);
  }, [filtersState]);

  const cardEntities = displayEntities;

  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(cardEntities.length / pageSize) || 1);
    if (cardPage > maxPage) setCardPage(maxPage);
  }, [cardEntities.length, pageSize, cardPage]);

  const paginatedEntities = useMemo(() => {
    const start = (cardPage - 1) * pageSize;
    return cardEntities.slice(start, start + pageSize);
  }, [cardEntities, cardPage, pageSize]);

  const savePageSize = (newPageSize: number) => {
    handlePreferences(PreferenceActions.SET, {
      name: PAGE_SIZE_ENTITIES,
      value: newPageSize,
    });
    setCardPage(1);
  };

  const tableColumns = useMemo(
    () =>
      isClientUser
        ? columns.filter((col) => col.key !== 'users')
        : columns,
    [columns, isClientUser]
  );

  const tableContextValue = useMemo(
    () => ({
      currentUser,
      hasAdvancedUserRights,
      members,
      membersLoading: !!membersLoading,
    }),
    [currentUser, hasAdvancedUserRights, members, membersLoading]
  );

  const pinContextValue = useMemo(
    () => ({ pinnedEntityIds }),
    [pinnedEntityIds]
  );

  const menuContextValue = useMemo(
    () => ({
      openContextMenuId,
      showThreeDotsMarker,
    }),
    [openContextMenuId, showThreeDotsMarker]
  );

  const captureContextValue = useMemo(
    () => ({
      statsByConnectionId,
      organisations,
    }),
    [statsByConnectionId, organisations]
  );

  const renderToolbarRight = () => (
    <Space wrap size={8} align="center">
      {!pwaMobileNav && (
        <Segmented
          value={viewMode}
          onChange={(v) => {
            const next = v as 'list' | 'card';
            userChangedViewLayoutRef.current = true;
            setViewMode(next);
            handlePreferences(PreferenceActions.SET, {
              name: ENTITIES_VIEW_LAYOUT,
              value: next,
            });
          }}
          options={[
            {
              label: t('dashboard.view-list'),
              value: 'list',
              icon: <UnorderedListOutlined />,
            },
            {
              label: t('users.view-card'),
              value: 'card',
              icon: <AppstoreOutlined />,
            },
          ]}
        />
      )}
      {!pwaMobileNav && (
        <ExportButton
          icon={<IconExport style={{ width: 20, height: 20 }} />}
          onClick={handleExportEntitiesList}
        >
          {t('organisation.export-list')}
        </ExportButton>
      )}
      {!pwaMobileNav &&
        currentUser &&
        AccountType[currentUser?.accountType] !== AccountType.CLIENT && (
          <FeatureDotBadge dotId="entities-create-group-button">
            <ScopeButton
              type="primary"
              onClick={createGroup}
              // @ts-ignore
              width="auto"
              height="32px"
              fontSize="13px"
              lineheight="20px"
              icon={<UsergroupAddOutlined />}
            >
              {t('group.create-group')}
            </ScopeButton>
          </FeatureDotBadge>
        )}
    </Space>
  );

  const renderTaxReturnForCard = (content: string, record: Entity) =>
    renderTaxReturn(content, record, { forCard: true });

  const renderUsersForCard = (record: Entity) =>
    renderUsers(record, { forCard: true });

  return (
    <EntityTableContext.Provider value={tableContextValue}>
    <EntityPinContext.Provider value={pinContextValue}>
    <EntityMenuContext.Provider value={menuContextValue}>
    <EntityCaptureContext.Provider value={captureContextValue}>
    <React.Fragment>
      <div
        className={`entities-page-content${
          pwaMobileNav ? ' scope-pwa-entities' : ''
        }`}
        style={{
          ...(pwaMobileNav
            ? {}
            : {
                padding: '40px',
                paddingTop: 10,
              }),
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          margin: 0,
          boxSizing: 'border-box',
        }}
      >
        {pwaMobileNav && (
          <Typography.Title level={4} className="scope-pwa-page-title">
            {t('organisation.entities')}
          </Typography.Title>
        )}
        {loading ? (
          <div
            style={{
              padding: '40px',
              paddingTop: 10,
              background: 'var(--color-surface-raised, #fff)',
              borderRadius: '4px',
            }}
          >
            {/* Header section skeleton */}
            <div
              style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Skeleton
                active
                paragraph={{ rows: 0 }}
                title={{ width: '120px', style: { height: '28px' } }}
              />
              <div
                style={{ display: 'flex', gap: '16px', alignItems: 'center' }}
              >
                <Skeleton.Input
                  active
                  size="default"
                  style={{
                    width: '250px',
                    height: '32px',
                    borderRadius: '8px',
                  }}
                />
                <Skeleton.Button
                  active
                  size="default"
                  style={{
                    width: '135px',
                    height: '40px',
                    borderRadius: '50px',
                  }}
                />
              </div>
            </div>

            {/* Filters section skeleton */}
            <div
              style={{
                marginBottom: '24px',
                paddingBottom: '16px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              <Skeleton.Input
                active
                size="small"
                style={{ width: '60px', height: '22px' }}
              />
              {[
                { width: 120 },
                { width: 100 },
                { width: 110 },
                { width: 130 },
                { width: 115 },
              ].map((filter, i) => (
                <Skeleton.Button
                  key={i}
                  active
                  size="small"
                  style={{
                    width: `${filter.width}px`,
                    height: '32px',
                    borderRadius: '8px',
                  }}
                />
              ))}
              <Skeleton.Input
                active
                size="small"
                style={{ width: '90px', height: '22px', marginLeft: 'auto' }}
              />
            </div>

            {viewMode === 'card' ? (
              <>
                <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Col xs={24} sm={12} lg={8} xl={6} key={i}>
                      <div
                        style={{
                          borderRadius: 10,
                          border: '1px solid var(--color-border, #dbeae3)',
                          background: 'var(--color-surface-raised, #ffffff)',
                          boxShadow: '0px 0px 10px 0px #0000000d',
                          padding: 24,
                          height: '100%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 12,
                          }}
                        >
                          <Space
                            align="start"
                            size={14}
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            <Skeleton.Avatar active size={32} shape="square" />
                            <Skeleton.Input
                              active
                              style={{ width: '70%', height: 16 }}
                            />
                          </Space>
                          <Skeleton.Button
                            active
                            size="small"
                            style={{ width: 24, height: 24 }}
                          />
                        </div>
                        <Skeleton
                          active
                          paragraph={{ rows: 5 }}
                          style={{ marginTop: 20 }}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
                <div
                  style={{
                    marginTop: 24,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Skeleton.Input active style={{ width: 280, height: 32 }} />
                </div>
              </>
            ) : (
              <>
                {/* Table container skeleton */}
                <div
                  style={{
                    border: '1px solid var(--color-border, #dbeae3)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: 'var(--color-surface-raised, #fff)',
                  }}
                >
                  {/* Table Header skeleton */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        '200px 1fr 120px 180px 150px 160px 50px',
                      gap: '16px',
                      padding: '16px 24px',
                      background: 'var(--color-surface, #f9fafa)',
                      borderBottom: '1px solid var(--color-border, #e8e8e8)',
                      alignItems: 'center',
                    }}
                  >
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '80px', height: '18px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '60px', height: '18px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '100px', height: '18px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '60px', height: '18px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '80px', height: '18px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '100px', height: '18px' }}
                    />
                    <div></div>
                  </div>

                  {/* Table Rows skeleton */}
                  <div style={{ padding: '8px 0' }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row) => (
                      <div
                        key={row}
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            '200px 1fr 120px 180px 150px 160px 50px',
                          gap: '16px',
                          padding: '16px 24px',
                          borderBottom: row < 10 ? '1px solid #f0f0f0' : 'none',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <Skeleton.Avatar
                            active
                            size={24}
                            shape="square"
                            style={{ borderRadius: '4px' }}
                          />
                          <Skeleton.Input
                            active
                            size="small"
                            style={{
                              flex: 1,
                              height: '16px',
                              width: '120px',
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexWrap: 'wrap',
                          }}
                        >
                          {row % 3 === 0 ? (
                            <Skeleton.Button
                              active
                              size="small"
                              style={{
                                width: '99px',
                                height: '25px',
                                borderRadius: '50px',
                              }}
                            />
                          ) : (
                            <>
                              <Skeleton.Input
                                active
                                size="small"
                                style={{ width: '140px', height: '16px' }}
                              />
                              {row % 2 === 0 && (
                                <Skeleton.Avatar
                                  active
                                  size={16}
                                  shape="circle"
                                />
                              )}
                            </>
                          )}
                        </div>
                        <Skeleton.Input
                          active
                          size="small"
                          style={{ width: '80px', height: '16px' }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {[1, 2].map((avatar) => (
                            <Skeleton.Avatar
                              key={avatar}
                              active
                              size={32}
                              shape="circle"
                              style={{ border: '2px solid #fff' }}
                            />
                          ))}
                          {row % 4 === 0 && (
                            <>
                              <Skeleton.Avatar
                                active
                                size={32}
                                shape="circle"
                                style={{
                                  background: 'var(--color-layout-bg, #E9F6F7)',
                                  border: '2px solid #fff',
                                }}
                              />
                              <Skeleton.Button
                                active
                                size="small"
                                style={{
                                  width: '25px',
                                  height: '26px',
                                  borderRadius: '50%',
                                  marginLeft: '4px',
                                }}
                              />
                            </>
                          )}
                        </div>
                        <Skeleton.Input
                          active
                          size="small"
                          style={{ width: '120px', height: '16px' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {row % 3 === 0 ? (
                            <Skeleton.Button
                              active
                              size="small"
                              style={{
                                width: '90px',
                                height: '30px',
                                borderRadius: '10px',
                              }}
                            />
                          ) : (
                            <Skeleton.Input
                              active
                              size="small"
                              style={{
                                width: '100px',
                                height: '24px',
                                borderRadius: '4px',
                              }}
                            />
                          )}
                        </div>
                        <div
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <Skeleton.Button
                            active
                            size="small"
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pagination skeleton */}
                <div
                  style={{
                    marginTop: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0',
                  }}
                >
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: '180px', height: '22px' }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <Skeleton.Button
                      active
                      size="small"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                      }}
                    />
                    <Skeleton.Button
                      active
                      size="small"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                      }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{
                        width: '50px',
                        height: '24px',
                        borderRadius: '6px',
                      }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '12px', height: '22px' }}
                    />
                    <Skeleton.Button
                      active
                      size="small"
                      style={{
                        width: '80px',
                        height: '32px',
                        borderRadius: '6px',
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {!pwaMobileNav &&
              (showCard ? (
                <EntityGuide
                  setShowCard={setShowCard}
                  handlePreferences={handlePreferences}
                />
              ) : (
                <Row
                  justify={'end'}
                  style={{
                    marginTop: -5,
                    marginBottom: 20,
                  }}
                >
                  <ExportButton
                    type="text"
                    onClick={() => {
                      setShowCard(true);
                      handlePreferences(PreferenceActions.SET, {
                        name: ENTITY_TUTORIAL_HIDE,
                        value: false,
                      });
                    }}
                    icon={<IconSupport style={{ width: 20, height: 20 }} />} //@ts-ignore
                    isGuide
                  >
                    {t('organisation.open-guide')}
                  </ExportButton>
                </Row>
              ))}
            {checkEntityMessage()}
            {!!dataSource && dataSource.length > 0 && (
              <>
                <div
                  className={
                    pwaMobileNav ? 'scope-pwa-entities-filters-row' : undefined
                  }
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: pwaMobileNav ? 'stretch' : 'center',
                    flexWrap: 'wrap',
                    gap: pwaMobileNav ? 12 : 8,
                    marginBottom: 3,
                  }}
                >
                  <div
                    className={
                      pwaMobileNav ? 'scope-pwa-entities-filters' : undefined
                    }
                    style={{ flex: 1, minWidth: 0, width: '100%' }}
                  >
                    <ScopeTableFilters
                      filters={[
                        'providerName',
                        'organisation',
                        'country',
                        'mappingName',
                        'users',
                      ]}
                      mainLocaleKey="organisation.filterLabels"
                      value={filtersState}
                      onChange={setFiltersState}
                      originalDataSource={dataSource}
                      tableDataSource={displayEntities}
                      embedded={false}
                      filterRowMarginBottom={pwaMobileNav ? 0 : 3}
                      matchTriggerWidth={pwaMobileNav}
                    />
                  </div>
                  {!pwaMobileNav && (
                    <div
                      style={{
                        flex: '0 1 auto',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8,
                        justifyContent: 'flex-end',
                      }}
                    >
                      {renderToolbarRight()}
                    </div>
                  )}
                </div>
                {viewMode === 'list' && (
                  <div className="entities-scope-table">
                    <style>{`
                    .entities-scope-table table {
                      table-layout: fixed;
                    }
                    .entities-scope-table .ant-table-column-sorters
                      > .ant-table-column-title,
                    .entities-scope-table .ant-table-cell,
                    .entities-scope-table .ant-space-item {
                      width: auto !important;
                      min-width: 0;
                    }
                    .entities-scope-table .ant-table-cell {
                      overflow: hidden;
                    }
                    .entities-scope-table .entities-col-tax-return {
                      overflow: visible !important;
                    }
                    .entities-scope-table .entities-col-tax-return .ant-space,
                    .entities-scope-table .entities-col-tax-return .ant-space-item {
                      overflow: visible !important;
                    }
                    .entities-scope-table .entities-col-users {
                      overflow: visible !important;
                    }
                    .entities-scope-table .entities-col-users .ant-space,
                    .entities-scope-table .entities-col-users .ant-space-item {
                      overflow: visible !important;
                    }
                    .entities-scope-table .entities-col-name .ant-space {
                      max-width: 100%;
                    }
                    .entities-scope-table .entities-col-connection {
                      overflow: hidden;
                      white-space: nowrap;
                    }
                    .entities-scope-table .ant-table-tbody > tr > td {
                      vertical-align: middle;
                    }
                    .entities-scope-table .entities-col-capture .entity-capture-backlog,
                    .entities-scope-table .entities-col-capture .entity-capture-backlog__metric {
                      display: inline-flex;
                      align-items: center;
                    }
                    .entities-scope-table .ant-table-cell-fix-left-last::after,
                    .entities-scope-table .ant-table-cell-fix-right-first::after {
                      box-shadow: none !important;
                    }
                  `}</style>
                    <ScopeTable //@ts-ignore
                      className="entities-scope-table"
                      rowKey="organisationId"
                      tableDataSource={displayEntities}
                      originalDataSource={dataSource}
                      columns={tableColumns}
                      defaultSort={['organisation', 'asc']}
                      pagination
                      rowsPerPage={pageSize}
                      enableColumnsCustomization
                      hideToolbarColumnsCustomizationButton
                      onColumnsChange={handleColumnsChange}
                      visibleColumns={effectiveVisibleColumns}
                      lockedColumns={lockedColumns}
                      columnOrder={columnOrder}
                      alwaysFixedColumns={ENTITIES_ALWAYS_FIXED_COLUMNS}
                      rowHeight={45}
                      haveBorder
                      showSizeChanger
                      getNewPageSize={savePageSize}
                      scroll={{ x: '100%' }}
                    />
                  </div>
                )}
                {viewMode === 'card' &&
                  (cardEntities.length === 0 ? (
                    <Typography.Text
                      style={{
                        display: 'block',
                        marginTop: 16,
                        color: 'var(--color-text-muted, #5a6a6a)',
                        textAlign: 'center',
                      }}
                    >
                      {t('organisation.entities-filter-no-results')}
                    </Typography.Text>
                  ) : (
                    <>
                      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        {paginatedEntities.map((record) => (
                          <Col
                            xs={24}
                            sm={12}
                            lg={8}
                            xl={6}
                            key={record.organisationId}
                          >
                            {pwaMobileNav ? (
                              <div
                                role="presentation"
                                onClick={(event) =>
                                  handlePwaEntityCardClick(
                                    event,
                                    record.organisationId
                                  )
                                }
                                style={{ cursor: 'pointer' }}
                              >
                                <EntityCard
                                  record={record}
                                  showUsersSection={showUsersSection}
                                  labels={{
                                    country: t('common.country'),
                                    mappingName: t('main.taxReturn'),
                                    users: t('main.users'),
                                    vat: t(
                                      'organisation.entity-tax-vat-column'
                                    ),
                                    capture: t(
                                      'organisation.capture-statistics-column'
                                    ),
                                  }}
                                  renderIdentity={renderEntityCardIdentity}
                                  renderCountry={renderCountry}
                                  renderTaxReturn={renderTaxReturnForCard}
                                  renderUsers={renderUsersForCard}
                                  renderVat={renderVat}
                                  renderCaptureStats={renderCaptureStats}
                                  renderActions={renderEntityActions}
                                />
                              </div>
                            ) : (
                              <EntityCard
                                record={record}
                                showUsersSection={showUsersSection}
                                labels={{
                                  country: t('common.country'),
                                  mappingName: t('main.taxReturn'),
                                  users: t('main.users'),
                                  vat: t('organisation.entity-tax-vat-column'),
                                  capture: t(
                                    'organisation.capture-statistics-column'
                                  ),
                                }}
                                renderIdentity={renderEntityCardIdentity}
                                renderCountry={renderCountry}
                                renderTaxReturn={renderTaxReturnForCard}
                                renderUsers={renderUsersForCard}
                                renderVat={renderVat}
                                renderCaptureStats={renderCaptureStats}
                                renderActions={renderEntityActions}
                              />
                            )}
                          </Col>
                        ))}
                      </Row>
                      <Pagination
                        style={{
                          marginTop: 24,
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                        current={cardPage}
                        pageSize={pageSize}
                        total={cardEntities.length}
                        onChange={(p) => setCardPage(p)}
                        showSizeChanger={scopeTablePageSizeSelectProps}
                        pageSizeOptions={['10', '20', '50', '100']}
                        onShowSizeChange={(_, size) => savePageSize(size)}
                      />
                    </>
                  ))}
              </>
            )}
          </>
        )}
      </div>
    </React.Fragment>
    </EntityCaptureContext.Provider>
    </EntityMenuContext.Provider>
    </EntityPinContext.Provider>
    </EntityTableContext.Provider>
  );
};
